import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { MessageCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/cn'
import { ROUTES } from '@/constants/routes'
import { STORAGE_KEYS } from '@/constants/app'
import { useAuth } from '@/hooks/useAuth'
import { useMessengerUnreadCount } from '@/modules/messenger/hooks/useMessenger'

interface MessengerIconProps {
  /** `header` = desktop app header only. Mobile uses `MessengerFab`. */
  variant?: 'header'
}

const FAB_SIZE = 48
const EDGE_GAP = 14
/** Pixels of movement before a press counts as a drag (not a tap). */
const DRAG_THRESHOLD_PX = 8
/** App bar (~3.5rem) + safe top — keep FAB below chrome. */
const TOP_CHROME = 56
/** Bottom nav (~3.75rem) + gap — keep FAB above the tab bar. */
const BOTTOM_CHROME = 60

type FabPos = { left: number; top: number }

function readSafeInset(side: 'top' | 'right' | 'bottom' | 'left'): number {
  if (typeof window === 'undefined') return 0
  const probe = getComputedStyle(document.documentElement).getPropertyValue(
    `env(safe-area-inset-${side}, 0px)`,
  )
  const n = Number.parseFloat(probe)
  return Number.isFinite(n) ? n : 0
}

function clampPos(pos: FabPos): FabPos {
  const safeTop = readSafeInset('top')
  const safeRight = readSafeInset('right')
  const safeBottom = readSafeInset('bottom')
  const safeLeft = readSafeInset('left')
  const minLeft = EDGE_GAP + safeLeft
  const maxLeft = Math.max(minLeft, window.innerWidth - FAB_SIZE - EDGE_GAP - safeRight)
  const minTop = TOP_CHROME + safeTop + EDGE_GAP
  const maxTop = Math.max(minTop, window.innerHeight - FAB_SIZE - BOTTOM_CHROME - safeBottom - EDGE_GAP)
  return {
    left: Math.min(maxLeft, Math.max(minLeft, pos.left)),
    top: Math.min(maxTop, Math.max(minTop, pos.top)),
  }
}

function defaultFabPos(): FabPos {
  if (typeof window === 'undefined') return { left: EDGE_GAP, top: EDGE_GAP }
  return clampPos({
    left: window.innerWidth - FAB_SIZE - EDGE_GAP,
    top: window.innerHeight - FAB_SIZE - BOTTOM_CHROME - EDGE_GAP,
  })
}

function readStoredFabPos(): FabPos | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.MESSENGER_FAB_POSITION)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<FabPos>
    if (typeof parsed.left !== 'number' || typeof parsed.top !== 'number') return null
    if (!Number.isFinite(parsed.left) || !Number.isFinite(parsed.top)) return null
    return clampPos({ left: parsed.left, top: parsed.top })
  } catch {
    return null
  }
}

function writeStoredFabPos(pos: FabPos) {
  try {
    localStorage.setItem(STORAGE_KEYS.MESSENGER_FAB_POSITION, JSON.stringify(pos))
  } catch {
    // private mode / quota — position still works for this session
  }
}

/** Desktop header entry — icon + unread badge → Messenger inbox. */
export function MessengerIcon({ variant = 'header' }: MessengerIconProps) {
  void variant
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const canView = hasPermission('messages.view')
  const { data } = useMessengerUnreadCount(canView)
  const unreadCount = data?.unreadCount ?? 0
  const badgeLabel = unreadCount > 99 ? '99+' : unreadCount

  if (!canView) return null

  return (
    <Button
      variant="ghost"
      size="icon"
      className="relative h-9 w-9 shrink-0 rounded-lg text-foreground hover:bg-secondary sm:h-10 sm:w-10"
      aria-label={unreadCount > 0 ? `Messages (${unreadCount} unread)` : 'Messages'}
      onClick={() => navigate(ROUTES.MESSENGER)}
    >
      <MessageCircle className="h-[18px] w-[18px]" strokeWidth={2} />
      {unreadCount > 0 ? (
        <span className="absolute -right-1.5 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[8px] font-bold leading-none tabular-nums text-white ring-2 ring-card">
          {badgeLabel}
        </span>
      ) : null}
    </Button>
  )
}

/**
 * Mobile-only floating Messages control — drag to reposition (persisted),
 * tap to open inbox. Hidden on Messenger routes and desktop.
 */
export function MessengerFab() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { hasPermission } = useAuth()
  const canView = hasPermission('messages.view')
  const { data } = useMessengerUnreadCount(canView)
  const unreadCount = data?.unreadCount ?? 0
  const badgeLabel = unreadCount > 99 ? '99+' : unreadCount
  const onMessenger = pathname === ROUTES.MESSENGER || pathname.startsWith(`${ROUTES.MESSENGER}/`)

  const [pos, setPos] = useState<FabPos>(() => readStoredFabPos() ?? defaultFabPos())
  const [dragging, setDragging] = useState(false)
  const dragRef = useRef<{
    pointerId: number
    startX: number
    startY: number
    originLeft: number
    originTop: number
    moved: boolean
  } | null>(null)

  const reclamp = useCallback(() => {
    setPos((prev) => {
      const next = clampPos(prev)
      if (next.left === prev.left && next.top === prev.top) return prev
      writeStoredFabPos(next)
      return next
    })
  }, [])

  useEffect(() => {
    window.addEventListener('resize', reclamp)
    window.addEventListener('orientationchange', reclamp)
    return () => {
      window.removeEventListener('resize', reclamp)
      window.removeEventListener('orientationchange', reclamp)
    }
  }, [reclamp])

  if (!canView || onMessenger) return null

  const onPointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return
    event.currentTarget.setPointerCapture(event.pointerId)
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originLeft: pos.left,
      originTop: pos.top,
      moved: false,
    }
  }

  const onPointerMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const state = dragRef.current
    if (!state || state.pointerId !== event.pointerId) return
    const dx = event.clientX - state.startX
    const dy = event.clientY - state.startY
    if (!state.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return
    state.moved = true
    setDragging(true)
    setPos(clampPos({ left: state.originLeft + dx, top: state.originTop + dy }))
  }

  const endPointer = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const state = dragRef.current
    if (!state || state.pointerId !== event.pointerId) return
    dragRef.current = null
    setDragging(false)
    try {
      event.currentTarget.releasePointerCapture(event.pointerId)
    } catch {
      // already released
    }
    if (state.moved) {
      setPos((prev) => {
        const next = clampPos(prev)
        writeStoredFabPos(next)
        return next
      })
      return
    }
    navigate(ROUTES.MESSENGER)
  }

  return (
    <div
      className="pointer-events-none fixed z-[42] md:hidden"
      style={{ left: pos.left, top: pos.top, width: FAB_SIZE, height: FAB_SIZE }}
    >
      <Button
        type="button"
        size="icon"
        className={cn(
          'pointer-events-auto relative h-12 w-12 touch-none rounded-full shadow-float',
          'bg-primary text-primary-foreground hover:bg-primary/90',
          'ring-2 ring-background/80',
          dragging && 'scale-[1.04] cursor-grabbing',
          !dragging && 'cursor-grab',
        )}
        aria-label={
          unreadCount > 0
            ? `Messages (${unreadCount} unread). Drag to move, tap to open.`
            : 'Messages. Drag to move, tap to open.'
        }
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
      >
        <MessageCircle className="h-5 w-5" strokeWidth={2.25} />
        {unreadCount > 0 ? (
          <span className="absolute -right-0.5 -top-0.5 flex h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold leading-none tabular-nums text-white ring-2 ring-background">
            {badgeLabel}
          </span>
        ) : null}
      </Button>
    </div>
  )
}
